import { Product } from "@/types/product";

export type StorefrontVariant = {
  id: number;
  title?: string;
  sku?: string | null;
  price?: string | number;
  available?: boolean;
  featured_image?: { src?: string } | null;
};

export type StorefrontProduct = {
  id: number;
  title?: string;
  handle?: string;
  body_html?: string;
  vendor?: string;
  product_type?: string;
  tags?: string[] | string;
  options?: Array<{
    id?: number;
    name?: string;
    position?: number;
    values?: string[];
  }>;
  images?: Array<{ id?: number; src?: string; alt?: string }>;
  variants?: StorefrontVariant[];
};

const STOP_WORDS = new Set([
  "and",
  "available",
  "best",
  "find",
  "for",
  "frame",
  "frames",
  "image",
  "images",
  "looking",
  "me",
  "need",
  "option",
  "options",
  "please",
  "show",
  "the",
  "three",
  "under",
  "up",
  "want",
  "with",
]);

const COLOR_TERMS = new Set([
  "black",
  "blue",
  "brown",
  "clear",
  "fuchsia",
  "gold",
  "gray",
  "green",
  "grey",
  "orange",
  "pink",
  "purple",
  "red",
  "silver",
  "white",
  "yellow",
]);

const getMaximumPrice = (query: string) => {
  const match = query.match(
    /(?:under|below|less than|max(?:imum)?(?: of)?|up to)\s*\$?\s*(\d+(?:\.\d+)?)/i,
  );
  return match ? Number(match[1]) : null;
};

const getSearchTerms = (query: string) =>
  Array.from(
    new Set(
      query
        .toLowerCase()
        .match(/[a-z0-9]+/g)
        ?.filter(
          (term) =>
            term.length > 1 && !STOP_WORDS.has(term) && !/^\d+$/.test(term),
        ) || [],
    ),
  );

const normalizeProduct = (
  product: StorefrontProduct,
  storeOrigin: string,
): Product => {
  const tags = Array.isArray(product.tags)
    ? product.tags
    : String(product.tags || "")
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean);
  const images = (product.images || [])
    .filter((image) => image.src)
    .map((image, index) => ({
      id: image.id || index,
      src: image.src || "",
      alt_text: image.alt || product.title || "",
      position: index + 1,
      product_id: product.id,
    }));
  const variants = (product.variants || []).map((variant) => ({
    id: variant.id,
    title: variant.title || "Default",
    sku: variant.sku || null,
    price: String(variant.price || "0.00"),
    available_for_sale: variant.available !== false,
    inventory_quantity: 0,
    option1: variant.title || "Default",
    attributes: [],
    image: variant.featured_image?.src
      ? { id: variant.id, url: variant.featured_image.src }
      : undefined,
  }));
  const availableVariant =
    variants.find((variant) => variant.available_for_sale) || variants[0];
  const now = new Date();

  return {
    id: product.id,
    primary_variant_id: availableVariant?.id || product.id,
    body_html: product.body_html || "",
    handle: product.handle || "",
    title: product.title || "Untitled product",
    store_url: storeOrigin,
    product_url: product.handle
      ? `${storeOrigin}/products/${product.handle}`
      : storeOrigin,
    created_at: now,
    images,
    options: (product.options || []).map((option, index) => ({
      id: option.id || index,
      name: option.name || "Option",
      position: option.position || index + 1,
      values: option.values || [],
    })),
    product_type: product.product_type || "",
    published_at: now,
    tags,
    updated_at: now,
    variants,
    vendor: product.vendor || "",
    images_embedded: images.length > 0,
    isProduct: true,
    timestamp: Date.now(),
  };
};

export const rankStorefrontProducts = (
  products: StorefrontProduct[],
  query: string,
  storeOrigin: string,
  limit = 3,
): Product[] => {
  const terms = getSearchTerms(query);
  const requestedColors = terms.filter((term) => COLOR_TERMS.has(term));
  const maximumPrice = getMaximumPrice(query);

  return products
    .map((rawProduct) => normalizeProduct(rawProduct, storeOrigin))
    .map((product) => {
      const availableVariants = product.variants.filter(
        (variant) => variant.available_for_sale,
      );
      const variantsInBudget = availableVariants.filter(
        (variant) =>
          maximumPrice === null || Number(variant.price) <= maximumPrice,
      );
      const searchable = [
        product.title,
        product.vendor,
        product.product_type,
        ...product.tags,
      ]
        .join(" ")
        .toLowerCase();
      const title = product.title.toLowerCase();
      const titleColors = Array.from(COLOR_TERMS).filter((color) =>
        title.includes(color),
      );
      const score = terms.reduce(
        (total, term) =>
          total +
          (title.includes(term) ? 5 : searchable.includes(term) ? 2 : 0),
        0,
      );

      return {
        product: {
          ...product,
          variants:
            variantsInBudget.length > 0 ? variantsInBudget : availableVariants,
          primary_variant_id:
            (variantsInBudget[0] || availableVariants[0])?.id ||
            product.primary_variant_id,
        },
        score,
        inBudget: maximumPrice === null || variantsInBudget.length > 0,
        matchesRequestedColor:
          requestedColors.length === 0 ||
          (titleColors.length > 0
            ? requestedColors.some((color) => title.includes(color))
            : requestedColors.some((color) => searchable.includes(color))),
      };
    })
    .filter(({ product, score, inBudget, matchesRequestedColor }) =>
      Boolean(
        product.variants.length > 0 &&
          inBudget &&
          matchesRequestedColor &&
          (score > 0 || terms.length === 0),
      ),
    )
    .sort((left, right) => right.score - left.score)
    .slice(0, limit)
    .map(({ product }) => product);
};

export const searchStorefrontCatalog = async (
  query: string,
  limit = 3,
): Promise<Product[]> => {
  const products = await loadStorefrontCatalog();

  return rankStorefrontProducts(products, query, window.location.origin, limit);
};

export const loadStorefrontCatalog = async (): Promise<StorefrontProduct[]> => {
  const response = await window.fetch("/products.json?limit=250", {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });

  if (!response.ok) {
    throw new Error(`Storefront catalog request failed (${response.status})`);
  }

  const payload = (await response.json()) as { products?: StorefrontProduct[] };
  return payload.products || [];
};
